import Grid2 from '@mui/material/Unstable_Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import GetRecord from 'src/lib/api/GetRecord';
import { APIS } from 'src/lib/constants/apis';
import { checkReadOnlyInUeberarbeitung, getFieldNamefromDataPath, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import { ART_DES_ANTRAGS, FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from '../../../../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import { BICValidation, IBANValidation, Required } from '../../../../../../../lib/validation/ValidationTypes';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import schemas
import { Typography } from '@mui/material';
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
const P = AntragSchema.attributes;
const ORGA_P = OrganisationSchema.attributes;

const BankVerbindungGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	const [allBankdaten, setAllBankdaten] = useState<any[]>([]);
	const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path, '') === ART_DES_ANTRAGS.Formantrag;

	const handleBankverbindungChange = (path: string, value: any) => {
		let appCopy = cloneDeep(application);

		const selectedOption = value;

		const selectedBankdaten = allBankdaten.find((bank: any) => bank.Bezeichnung === selectedOption);

		const appKontoInhaberPath = P.Antragstellende_Organisation_Bankverbindung_Kontoinhaber.path;
		const appGeldinstitutPath = P.Antragstellende_Organisation_Bankverbindung_Geldinstitut.path;
		const appIbanPath = P.Antragstellende_Organisation_Bankverbindung_IBAN.path;
		const appBicPath = P.Antragstellende_Organisation_Bankverbindung_BIC.path;

		set(appCopy, path, value);
		set(
			appCopy,
			appKontoInhaberPath,
			get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_Kontoinhaber.path), ''),
		);
		set(
			appCopy,
			appGeldinstitutPath,
			get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_Geldininstitut.path), ''),
		);
		set(appCopy, appIbanPath, get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_IBAN.path), ''));
		set(appCopy, appBicPath, get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_BIC.path), ''));

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	useEffect(() => {
		const fetchBankdaten = async () => {
			try {
				const orgaId = get(application, `${P.Antragstellende_Organisation.path}.id`, 0);
				if (Number(orgaId) === 0) return;
				const res = await GetRecord(APIS.Organisation, orgaId, [getFieldNamefromDataPath(ORGA_P.Bankdaten.path)]);
				const resData = removeNullDeep(removeDataKeys(get(res, 'data', {})));

				const allBankdaten = get(resData, ORGA_P.Bankdaten.path, []);
				setAllBankdaten(allBankdaten);
			} catch (err: any) {
				console.error('Error fetching bankdaten:', err);
			}
		};
		fetchBankdaten();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Bankverbindung' description={PLATZHALTER_TEXT} />
			{isFormantrag ?
				<Typography>
					<strong>Bitte beachten Sie:</strong> Im Falle einer Änderung der Bankverbindung für das Projekt ist ein aktualisiertes Stammdatenblatt
					herunterzuladen, zu unterschreiben, einzusenden und hochzuladen.
				</Typography>
			:	<Typography>
					Im Falle einer Änderung der Bankverbindung für das Projekt ist das neue Stammdatenblatt unter IV. Stammdatenblatt herunterzuladen, zu
					unterschreiben und hochzuladen.
				</Typography>
			}
			<CreateForm
				formStruc={formStrucBankdatenAuswahl(allBankdaten)}
				formValues={application}
				updateFormValuesFunc={(key: string, value: any) => handleBankverbindungChange(key, value)}
				readOnly={updatedReadOnly}
			/>
			<Grid2 spacing={6} container>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucBankdatenSpalte1()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={true}
					/>
				</Grid2>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucBankdatenSpalte2(application)}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={true}
					/>
				</Grid2>
			</Grid2>
		</>
	);
};

export default BankVerbindungGlobal;

const formStrucBankdatenAuswahl = (allBankdaten?: any[]) => {
	const bankOptions = (allBankdaten || []).map((bank: any) => ({ label: bank.Bezeichnung, value: bank.Bezeichnung })) || [];
	// Wenn noch nicht das erste mal gepflegt ist, dann leeren eintrag rausfiltern -> sonst 404 Fehler bei Auswahl
	const filteredOptions = bankOptions.filter((opt) => opt.label !== '' || opt.value !== '');
	const struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Antragstellende_Organisation_Bankverbindung_Bezeichnung,
					validation: [{ validateFunction: Required }],
					type: FORM_FIELDS_TYPES.singleSelect,
					options: filteredOptions,
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucBankdatenSpalte1 = () => {
	return [
		{
			fields: [P.Antragstellende_Organisation_Bankverbindung_Kontoinhaber, P.Antragstellende_Organisation_Bankverbindung_Geldinstitut],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
};
const formStrucBankdatenSpalte2 = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Antragstellende_Organisation_Bankverbindung_IBAN,
					validation: [{ validateFunction: Required }, { validateFunction: IBANValidation }],
				},
			],
			userProps: { columnToDivide: 1 },
		},
		{
			fields: [
				{
					...P.Antragstellende_Organisation_Bankverbindung_BIC,
					validation: [{ validateFunction: Required }, { validateFunction: BICValidation }],
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	const iban = get(application, P.Antragstellende_Organisation_Bankverbindung_IBAN.path, '');
	if (typeof iban === 'string' && iban.includes('DE')) {
		struc.pop();
	}

	return struc;
};

export const getFormStrucBankVerbindungGlobal = (application: any) => {
	return concat(formStrucBankdatenSpalte1(), formStrucBankdatenSpalte2(application));
};
